import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { JSX, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import useTransientStore from "./transcientStore";
import { getUserDetails } from "../services/UserService";
import { clientId } from "./msalConfig";
import { AUTH_ENABLED } from "./authConfig";

export default function PrivateWrapper({
  children,
}: {
  children?: JSX.Element;
}): JSX.Element | null {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, accounts, instance } = useMsal();

  const token = useTransientStore((state) => state.token);
  const setToken = useTransientStore((state) => state.setToken);
  const setIsUserRegistered = useTransientStore(
    (state) => state.setIsUserRegistered,
  );
  const isRegistered = useTransientStore((state) => state.isUserRegistered); // boolean | null
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const isUserRegistered = async (userEmail: string) => {
    const userDetails = await getUserDetails(userEmail);

    if (userDetails) {
      setIsUserRegistered(true);
    } else {
      setIsUserRegistered(false);
    }
  };

  useEffect(() => {
    const checkToken = async () => {
      if (accounts.length > 0) {
        const account = accounts[0];
        const expiryDate = account.idTokenClaims?.exp
          ? account.idTokenClaims.exp * 1000
          : 0;

        if (expiryDate < Date.now()) {
          // Token has expired, attempt silent token acquisition
          try {
            await instance.acquireTokenSilent({
              account,
              scopes: [`api://${clientId}/access_as_user`],
            });

            setIsTokenValid(true); // Token refreshed, mark it as valid
          } catch (error) {
            console.error(
              "Silent token acquisition failed, redirecting to login.",
              error,
            );
            setIsTokenValid(false); // Token is invalid
            navigate("/");
          }
        } else {
          setIsTokenValid(true); // Token is valid
        }
        await isUserRegistered(accounts[0].username); // Check if user is registered
      }
      setIsLoading(false); // Finish loading
    };

    checkToken();
  }, [accounts, instance]);

  // When auth is disabled, allow access immediately
  if (!AUTH_ENABLED) {
    return <>{children}</>;
  }

  if (
    isLoading ||
    inProgress !== InteractionStatus.None ||
    isRegistered === null
  ) {
    return null; // You can return a loading spinner here
  }

  // Block access if user ID is invalid (-1 or 0 means user not found in database)
  if (currentUserId <= 0) {
    return <Navigate to="/" />;
  }

  if (isAuthenticated && isTokenValid && isRegistered === true) {
    return <>{children}</>;
  }

  return <Navigate to="/" />;
}
